import { useContext, useRef, useState } from "react";
import {
    Button,
    Grid,
    Popover,
    Segmented,
    Select,
    Switch,
    theme,
    Typography,
} from "antd";
import {
    DoubleRightOutlined,
    PaperClipOutlined,
    SettingOutlined,
    StopOutlined,
} from "@ant-design/icons";

import FlexBox from "../../atoms/FlexBox";
import FlexCol from "../../atoms/FlexCol";
import { AIContext } from "../../../providers/AIContext";
import { MessageContext } from "../../../providers/MessageContext";
import {
    AGENT_FILE_ACCEPT_ATTR,
    agentReasoningEffortOptions,
    agentThinkingModeOptions,
    modelCapabilities,
    unstructuredModelOptions,
} from "#types";
import { devFlag } from "../../../config";
import ModelInputAttachments from "./ModelInputAttachments";
import PromptComposer, { type PromptComposerHandle } from "./PromptComposer";
import { useAttachments } from "./useAttachments";
import MappingReviewDevLauncher from "../review/MappingReviewDevLauncher";

type InputMode = "chat" | "agent";

export default function ModelInput() {
    const { messageApi } = useContext(MessageContext);
    const screen = Grid.useBreakpoint();
    const { token } = theme.useToken();
    const [mode, setMode] = useState<InputMode>("agent");
    const [sanitizeEmail, setSanitizeEmail] = useState(true);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const composerRef = useRef<PromptComposerHandle>(null);

    const {
        streamModel,
        streamModelAgentic,
        cancelStream,
        isLoading,
        setIsLoading,
        paramsLoading,
        agenticProgress,
        modelParams,
        setModelParams,
        attachFiles,
    } = useContext(AIContext);

    const { attachments, removeAttachment, removeAllAttachments } =
        useAttachments();

    const isRunning = isLoading || !!agenticProgress?.isRunning;

    const capabilities = modelCapabilities(modelParams.model);

    const handleFileSelect = async (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const files = Array.from(event.target.files ?? []);
        event.target.value = "";
        if (files.length === 0) return;
        await attachFiles(files, { sanitizeEmail });
    };

    const handleSubmit = async (prompt: string) => {
        setIsLoading(true);
        try {
            if (mode === "agent" || attachments.length > 0) {
                await streamModelAgentic(prompt, prompt);
            } else {
                await streamModel(prompt);
            }
        } catch (e) {
            if (e instanceof Error) {
                messageApi.error(e.message);
            }
            console.error("Error in model input:", e);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <FlexCol gap={5} alignStart>
            <PromptComposer
                ref={composerRef}
                onSubmit={handleSubmit}
                disabled={isRunning || paramsLoading}
            />
            <FlexBox stretch justifyEnd alignEnd gap={5}>
                <MappingReviewDevLauncher />
                {devFlag && (
                    <Popover
                        trigger="click"
                        placement="topLeft"
                        content={
                            <FlexCol noGrow alignStart gap={10}>
                                <Segmented
                                    aria-label="input-mode-select"
                                    options={[
                                        { value: "chat", label: "Chat" },
                                        { value: "agent", label: "Agent" },
                                    ]}
                                    value={mode}
                                    onChange={(value) =>
                                        setMode(value as InputMode)}
                                />
                                <Select
                                    aria-label="agent-model-select"
                                    value={modelParams.model}
                                    options={[...unstructuredModelOptions]}
                                    styles={{
                                        popup: { root: { minWidth: 200 } },
                                    }}
                                    onChange={(model) =>
                                        setModelParams((prev) => ({
                                            ...prev,
                                            model,
                                        }))}
                                />
                                {capabilities.thinking && (
                                    <Select
                                        aria-label="agent-thinking-mode-select"
                                        value={modelParams.thinkingMode ??
                                            "standard"}
                                        options={[...agentThinkingModeOptions]}
                                        styles={{
                                            popup: { root: { minWidth: 200 } },
                                        }}
                                        onChange={(thinkingMode) =>
                                            setModelParams((prev) => ({
                                                ...prev,
                                                thinkingMode,
                                            }))}
                                    />
                                )}
                                {capabilities.effort && (
                                    <Select
                                        aria-label="agent-reasoning-effort-select"
                                        value={modelParams.reasoningEffort ??
                                            "medium"}
                                        options={[
                                            ...agentReasoningEffortOptions,
                                        ]}
                                        styles={{
                                            popup: { root: { minWidth: 200 } },
                                        }}
                                        onChange={(reasoningEffort) =>
                                            setModelParams((prev) => ({
                                                ...prev,
                                                reasoningEffort,
                                            }))}
                                    />
                                )}
                                <FlexBox noGrow gap={5}>
                                    <Switch
                                        aria-label="sanitize-email-toggle"
                                        checked={sanitizeEmail}
                                        onChange={setSanitizeEmail}
                                        size="small"
                                    />
                                    <Typography.Text type="secondary">
                                        Sanitize email
                                    </Typography.Text>
                                </FlexBox>
                            </FlexCol>
                        }
                    >
                        <Button
                            aria-label="agent-dev-controls"
                            icon={<SettingOutlined />}
                            style={{ boxShadow: token.boxShadow }}
                            type="primary"
                        />
                    </Popover>
                )}
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={AGENT_FILE_ACCEPT_ATTR}
                    onChange={handleFileSelect}
                    aria-label="file-attachment-input"
                    style={{ display: "none" }}
                />
                <ModelInputAttachments
                    attachments={attachments}
                    onRemove={removeAttachment}
                    onRemoveAll={removeAllAttachments}
                />
                <Button
                    aria-label="attach-file"
                    icon={<PaperClipOutlined />}
                    style={{ boxShadow: token.boxShadow }}
                    onClick={() => fileInputRef.current?.click()}
                />
                <Button
                    aria-label={isRunning ? "cancel-prompt" : "send-prompt"}
                    icon={isRunning
                        ? <StopOutlined />
                        : <DoubleRightOutlined />}
                    danger={isRunning}
                    loading={!isRunning && paramsLoading}
                    disabled={!isRunning && paramsLoading}
                    onClick={isRunning ? cancelStream : () => composerRef.current?.submit()}
                    style={{ 
                        boxShadow: token.boxShadow,
                        minWidth: 120,
                    }}
                >
                    {screen.sm && (isRunning ? "CANCEL" : "ENTER")}
                </Button>
            </FlexBox>
        </FlexCol>
    );
}
